import { Injectable } from '@angular/core';
import { doc, DocumentReference, Firestore, updateDoc, docData  } from '@angular/fire/firestore';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { AuthStoreDispatcher } from '../state/auth/auth.dispatcher';
import { filter, pluck, map, switchMap, takeUntil } from 'rxjs/operators';
import { ProfileData } from '../state/profile/profile.state';
import { FireSubService } from './fireSub.service';
import { NftAsset } from '../state/crypto/crypto.state';

@Injectable({ providedIn: 'root' })
export class ProfileService {
    private myUserId$: Observable<string>;

    constructor(
        private firestore: Firestore,
        private authStore: AuthStoreDispatcher,
        private fireSubService: FireSubService,
    ) {
        this.myUserId$ = this.authStore.getState().pipe(
            filter((data) => data != null && data.userData != null),
            pluck('userData', 'uid'),
        );
    }

    public getMyProfile(): Observable<ProfileData> {
        return this.myUserId$.pipe(
            switchMap((id) => {
                return this.getProfile(id);
            }),
        );
    }

    public getMyUserId(): Observable<string> {
        return this.myUserId$;
    }

    public getProfile(id: string): Observable<ProfileData> {
        if (id == null) {
            throw new Error(`Cannot get profile of id === null`);
        }
        const docReference = doc(this.firestore, `users/${id}`) as DocumentReference<any>;
        const profile$ = docData(docReference);
        const publicProfileData$ = this.getPublicUserInfoObservable(id);
        return combineLatest([profile$, publicProfileData$])
            .pipe(
                takeUntil(this.fireSubService.unsubscriber),
                filter((data) => data[0] != null && data[1] != null),
                map(([profile, publicProfileData]) => {
                    if (profile == null) {
                        throw new Error(`Cannot find profile for ${id}`);
                    }
                    return {
                        ...publicProfileData,
                        lastResponseTime: profile.lastResponseTime,
                        email: profile.email,
                        uid: profile.uid,
                        lastNameChange: profile.lastNameChange ? profile.lastNameChange : null,
                        diamonds: profile.diamonds ? profile.diamonds : 0,
                        dateCreated: profile.dateCreated,
                        lastestResponse: profile.lastestResponse,
                        gpsStatus: profile.gpsStatus,
                        golds: profile.golds ? profile.golds : 0,
                        sweepbucks: profile.sweepbucks ? profile.sweepbucks : 0,
                        jackpotTicket: profile.jackpotTicket ? profile.jackpotTicket : 0,
                        termsAccepted: profile.termsAccepted ?? false,
                        streak: profile.streak ? profile.streak : 1,
                        phoneNumber: profile.phoneNumber ? profile.phoneNumber : '',
                        test2faEnabled: profile.test2faEnabled ? profile.test2faEnabled : false,
                        emailVerified: profile.emailVerified ? profile.emailVerified : false,
                        zendeskEndUserId: profile.zendeskEndUserId ? profile.zendeskEndUserId : null,
                        pendingRequests: profile.pendingRequests ? profile.pendingRequests : [],
                        profileSetupNeeded: profile.profileSetupNeeded ?? null,
                    };
                }),
            );
    }

    public getPublicUserInfo(id: string): Promise<PublicUserInfo> {
        return firstValueFrom(this.getPublicUserInfoObservable(id));
    }

    public getPublicUserInfoObservable(id: string): Observable<PublicUserInfo> {
        if (id == null) {
            throw new Error(`Cannot get profile of id === null`);
        }

        const isBot = id.toLowerCase().includes('bot_');

        if (!isBot) {
            const humanInfo$ = this.getHumanUserInfoOservable(id);
            return humanInfo$.pipe(
                map(humanInfo => {
                    return {
                        ...humanInfo,
                        useNftAsAvatar: humanInfo.useNftAsAvatar || false,
                        defaultAvatar: humanInfo.defaultAvatar == undefined ? true : humanInfo.defaultAvatar,
                        isHuman: true,
                    };
                }),
            );
        } else {
            const botInfo$ = this.getBotUserInfoObservable(id);
            return botInfo$.pipe(
                map(botInfo => {
                    return {
                        ...botInfo,
                        defaultAvatar: true,
                        useNftAsAvatar: false,
                    }
                })
            );
        }
    }

    private getHumanUserInfoOservable(id: string): Observable<HumanUserInfo> {
        const docReference = doc(this.firestore, `users/${id}/public/data`) as DocumentReference<HumanUserInfo>;
        return docData<HumanUserInfo>(docReference)
            .pipe(
                takeUntil(this.fireSubService.unsubscriber),
                map((userData) => {
                    if (userData) return {...userData};
                    return {
                        displayName: '[Deleted User]',
                        shortID: 0,
                        role: 'user',
                        photoURL: 'assets/icon/thumbnail.png',
                    }
                }),
            );
    }

    private getBotUserInfoObservable(id: string): Observable<BotUserInfo> {
        const docRef = doc(this.firestore, `bots/${id}/public/data`) as DocumentReference<BotUserInfo>;
        return docData<BotUserInfo>(docRef)
            .pipe(
                takeUntil(this.fireSubService.unsubscriber),
                map((botData) => {
                    if (botData) return {...botData};
                    return {
                        displayName: '[Deleted User]',
                        shortID: 0,
                        role: 'user',
                        photoURL: 'assets/icon/thumbnail.png',
                        isHuman: false,
                    }
                }),
            );
    }

    public async updateCurrentClubID(clubID: string) {
        const userId = await this.authStore.getMyID();
        const docReference = doc(this.firestore, `users/${userId}`);
        updateDoc(docReference, {
            currentClubId: clubID
        });
    }

    public setUserTermsToAccepted(myID: string) {
        return updateDoc(doc(this.firestore, `users/${myID}`),{
            termsAccepted: true
        });
    }

    public setProfileSetupToDone(myId: string): Promise<void> {
        return updateDoc(doc(this.firestore, `users/${myId}`), {
            profileSetupNeeded: false,
        });
    }
}

export interface PublicUserInfo extends HumanUserInfo, BotUserInfo {
    defaultAvatar: boolean;
    useNftAsAvatar: boolean;
}

export interface HumanUserInfo extends CommonUserInfo {
    defaultAvatar?: boolean;
    useNftAsAvatar?: boolean;
    nft?: NftAsset;
}

export interface BotUserInfo extends CommonUserInfo {
    isHuman: boolean;
}

export interface CommonUserInfo {
    displayName: string;
    shortID: number;
    role: string;
    photoURL: string | number;
}
